import { HttpClient } from '@sphere/shared/data-access/http'
import type {
  ActivityType,
  GenerateActivityResponseDto,
  ListActivityTypesRequestDto,
  ActivityTypeConfig,
  CreateActivityTypeRequestDto,
  UpdateActivityTypeRequestDto,
  PreviewActivityWithAIResponseDto,
} from '@sphere/learning/domain'
import type {
  ApproveActivityFromAIDto,
  CreateActivityTeachingAssignmentDto,
  CreateActivityUserAssignmentDto,
  GenerateActivityWithAIDto as GenerateActivityRequest,
  PreviewActivityWithAIDto,
  CreateActivityDto,
  UpdateActivityDto,
  ListActivitiesDto,
  SetStateDto,
  Activity,
} from '@sphere/learning/domain'
import type { IOffsetPaginationResponse } from '@sphere/shared/domain'
import type {
  RemediationActivitiesResponse,
  StudentDifficultiesInsightResponse,
} from '@sphere/learning/domain'

export const activitiesClient = {
  list: (params: ListActivitiesDto = {}) =>
    HttpClient.get<IOffsetPaginationResponse<Activity>>(
      '/learning/activities',
      { params }
    ),

  listExercises: (params: ListActivitiesDto = {}) =>
    HttpClient.get<IOffsetPaginationResponse<Activity>>(
      '/learning/activities/exercises',
      { params }
    ),

  listRevisions: (params: ListActivitiesDto = {}) =>
    HttpClient.get<IOffsetPaginationResponse<Activity>>(
      '/learning/activities/revisions',
      { params }
    ),

  getRemediationActivities: (params?: {
    since_days?: number
    subject_id?: string
    weak_limit?: number
    activity_limit?: number
  }) =>
    HttpClient.get<RemediationActivitiesResponse>(
      '/learning/users/me/remediation-activities',
      { params }
    ),

  getStudentDifficultiesInsight: (
    userId: string,
    params?: {
      teaching_id?: string
      subject_id?: string
      since_days?: number
      weak_limit?: number
      activity_limit?: number
    }
  ) =>
    HttpClient.get<StudentDifficultiesInsightResponse>(
      `/learning/users/${encodeURIComponent(userId)}/difficulties-insight`,
      { params }
    ),

  getById: (id: string) =>
    HttpClient.get<Activity>(`/learning/activities/${id}`),

  create: (data: CreateActivityDto) =>
    HttpClient.post<Activity>('/learning/activities', data),

  update: (id: string, data: UpdateActivityDto) =>
    HttpClient.patch<Activity>(`/learning/activities/${id}`, data),

  delete: (id: string) => HttpClient.delete<void>(`/learning/activities/${id}`),

  clone: (id: string) =>
    HttpClient.post<Activity>(`/learning/activities/${id}/clone`),

  setState: (id: string, data: SetStateDto) =>
    HttpClient.patch<Activity>(`/learning/activities/${id}/state`, data),

  publish: (id: string) =>
    HttpClient.post<Activity>(`/learning/activities/${id}/publish`),

  setVisibility: (id: string, visibility: string) =>
    HttpClient.patch<Activity>(`/learning/activities/${id}/visibility`, {
      visibility,
    }),

  assignToClass: (data: CreateActivityTeachingAssignmentDto) =>
    HttpClient.post<void>(
      `/learning/activities/${data.activityId}/assign/class`,
      data
    ),

  assignToUsers: (data: CreateActivityUserAssignmentDto) =>
    HttpClient.post<void>(
      `/learning/activities/${data.activityId}/assign/users`,
      data
    ),

  getTypes: () =>
    HttpClient.get<{
      data: ActivityType[]
      pageInfo: {
        limit: number
        sort: string
        order: string
        cursor: string | null
        hasNextPage: boolean
        nextCursor: string | null
        hasPreviousPage: boolean
        prevCursor: string | null
        endCursor: string
        totalCount: number
      }
    }>('/learning/activity-types'),
  getType: (type: string) =>
    HttpClient.get<ActivityType>(`/learning/activity-types/${type}`),
  
  generateWithAI: (request: GenerateActivityRequest) =>
    HttpClient.post<GenerateActivityResponseDto>(
      `/learning/activities/generate-with-ai`,
      request
    ),

  previewWithAI: (request: PreviewActivityWithAIDto) =>
    HttpClient.post<PreviewActivityWithAIResponseDto>(
      `/learning/activities/generate-with-ai/preview`,
      request
    ),

  approveFromAI: (request: ApproveActivityFromAIDto) =>
    HttpClient.post<Activity>(`/learning/activities/approve-from-ai`, request),
}

export const activityTypesClient = {
  list: (params: ListActivityTypesRequestDto = {}) =>
    HttpClient.get<{
      data: ActivityType[]
      pageInfo: {
        page: number
        limit: number
        totalCount: number
        totalPages: number
        sort: string
        order: string
      }
    }>('/learning/activity-types', { params }),

  getById: (id: string) =>
    HttpClient.get<ActivityType>(`/learning/activity-types/${id}`),

  create: (data: CreateActivityTypeRequestDto) =>
    HttpClient.post<ActivityType>('/learning/activity-types', data),

  update: (id: string, data: UpdateActivityTypeRequestDto) =>
    HttpClient.patch<ActivityType>(`/learning/activity-types/${id}`, data),

  delete: (id: string) =>
    HttpClient.delete<void>(`/learning/activity-types/${id}`),

  getConfig: (id: string) =>
    HttpClient.get<ActivityTypeConfig>(`/learning/activity-types/${id}/config`),
}
